import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/client';
import Modal from '../components/Modal';
import { useAuth } from '../context/AuthContext';

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value ?? '0');
  return n.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

export default function DeleteAccountPage() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const res = await api.get('/auth/account-deletion/');
        if (!cancelled) setInfo(res.data);
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.detail || 'Не удалось проверить возможность удаления');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  async function confirmDelete() {
    setSubmitting(true);
    setError('');
    try {
      await api.post('/auth/account-deletion/');
      setModalOpen(false);
      try {
        await logout();
      } catch {
        // сессия уже закрыта на сервере
      }
      navigate('/login', {
        replace: true,
        state: { accountDeletionRequested: true },
      });
    } catch (e) {
      const data = e.response?.data;
      setError(
        (typeof data?.detail === 'string' && data.detail)
          || data?.error
          || 'Не удалось подать заявку на удаление',
      );
      if (data?.block_reason) {
        setInfo((prev) => ({
          ...(prev || {}),
          can_delete: false,
          block_reason: data.block_reason,
          balance: data.balance ?? prev?.balance,
          pending_amount: data.pending_amount ?? prev?.pending_amount,
          effective_balance: data.effective_balance ?? prev?.effective_balance,
          has_pending_payment: data.block_reason === 'pending_payment'
            ? true
            : prev?.has_pending_payment,
        }));
      }
      setModalOpen(false);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        <p>Проверка аккаунта...</p>
      </div>
    );
  }

  const canDelete = info?.can_delete && !user?.is_staff;
  const reason = info?.block_reason || (user?.is_staff ? 'staff' : null);

  return (
    <div className="profile-page delete-account-page">
      <div className="page-header">
        <h1>Удалить аккаунт навсегда</h1>
        <p className="page-subtitle">
          <Link to="/profile">← Назад в профиль</Link>
        </p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="delete-account-card">
        <h2>Что произойдёт</h2>
        <ul className="delete-account-list">
          <li>
            Персональные данные будут удалены безвозвратно: имя, логин, электронная почта,
            телефон, привязка к мессенджерам.
          </li>
          <li>
            Удаляются все сообщения, комментарии к решениям и журнал оплаты
            (только при нулевом балансе с учётом неподтверждённых занятий).
          </li>
          <li>
            Остаётся обезличенная статистика решений задач и текст решений.
          </li>
          <li>
            Сразу после подтверждения вход на сайт будет заблокирован.
            Аккаунт будет удалён в течение 10 дней.
          </li>
        </ul>
        <p className="text-secondary">
          Пока заявка обрабатывается вручную: администратор получит уведомление
          и примет окончательное решение. Восстановить доступ самостоятельно нельзя.
        </p>
      </div>

      {reason === 'staff' && (
        <div className="alert alert-warning">
          Сотрудники сайта не могут удалить аккаунт этой кнопкой.
          Обратитесь к другому администратору.
        </div>
      )}

      {reason === 'positive_balance' && (
        <div className="alert alert-warning">
          <p>
            На счёте есть деньги
            {info?.effective_balance != null
              ? ` (эффективный баланс ${formatMoney(info.effective_balance)} ₽)`
              : ''}
            . Автоматический вывод недоступен.
          </p>
          <p>
            Напишите администратору сайта, чтобы вывести средства, или разберитесь
            с балансом на странице оплаты.
          </p>
          {(Number(info?.pending_amount) > 0) && (
            <p className="text-secondary">
              На счёте {formatMoney(info.balance)} ₽, неподтверждённые занятия:{' '}
              {formatMoney(info.pending_amount)} ₽.
            </p>
          )}
          <div className="delete-account-actions">
            <Link to="/billing" className="btn btn-outline">К балансу</Link>
            <Link to="/my-messages" className="btn btn-outline">Написать администратору</Link>
          </div>
        </div>
      )}

      {reason === 'negative_balance' && (
        <div className="alert alert-warning">
          <p>
            На счёте есть долг
            {info?.effective_balance != null
              ? ` (эффективный баланс ${formatMoney(info.effective_balance)} ₽)`
              : ''}
            . Сначала погасите задолженность.
          </p>
          {(Number(info?.pending_amount) > 0) && (
            <p className="text-secondary">
              На счёте {formatMoney(info.balance)} ₽, неподтверждённые занятия:{' '}
              {formatMoney(info.pending_amount)} ₽.
            </p>
          )}
          <div className="delete-account-actions">
            <Link to="/billing" className="btn btn-primary">Погасить долг</Link>
          </div>
        </div>
      )}

      {reason === 'pending_payment' && (
        <div className="alert alert-warning">
          <p>
            На счёте есть незавершённый платёж. Дождитесь результата оплаты
            или отмените платёж, затем попробуйте снова.
          </p>
          <div className="delete-account-actions">
            <Link to="/billing" className="btn btn-outline">К балансу</Link>
          </div>
        </div>
      )}

      {canDelete && (
        <div className="delete-account-actions" style={{ marginTop: '1.25rem' }}>
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => setModalOpen(true)}
          >
            Удалить аккаунт навсегда
          </button>
          <button
            type="button"
            className="btn btn-outline"
            onClick={() => navigate('/profile')}
          >
            Отмена
          </button>
        </div>
      )}

      <Modal
        open={modalOpen}
        title="Подтверждение удаления"
        onClose={() => {
          if (!submitting) setModalOpen(false);
        }}
      >
        <p>
          Вход будет закрыт прямо сейчас. Аккаунт удалят в течение 10 дней.
          Восстановить доступ можно только через администратора.
        </p>
        <p className="text-secondary">
          Это действие нельзя отменить самостоятельно.
        </p>
        <div className="delete-account-actions" style={{ marginTop: '1rem' }}>
          <button
            type="button"
            className="btn btn-danger"
            disabled={submitting}
            onClick={confirmDelete}
          >
            {submitting ? 'Удаление…' : 'Я понял, удалить'}
          </button>
          <button
            type="button"
            className="btn btn-outline"
            disabled={submitting}
            onClick={() => setModalOpen(false)}
          >
            Отмена
          </button>
        </div>
      </Modal>
    </div>
  );
}
